import { BaseTexture, Rectangle, Texture } from "pixi.js"
import { TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import type { RgbaImage } from "game-common/room/autotile_compose"
import { WALL_AUTOTILE_DEFINITIONS, WallAutotileDefinition } from "game-common/room/autotile_library"
import { WallTrimGround, WallTrimPainter, WallTrimStack, WallTrimStorey } from "game-common/room/wall_trims"
import { STOREY_RISE_PX } from "game-common/room/ember/two_storey_house"
import { contentTexture } from "../content_textures"
import { waterAt } from "../water_presence"
import { grassAt } from "./grass_feet"

/**
 * The client side of wall trims (game-common room/wall_trims.ts): the storey stack a renderer draws, as the
 * painter reads it; one painter per stack and state of its walls, shared by every storey's renderer; and an
 * atlas the trimmed cells are baked into, so a castle's hundreds of distinct cells still draw in few batches.
 * Nothing here runs in the frame loop: a cell is baked when its storey renders.
 */

// ---------------------------------------------------------------------------------------------
// Pixels
// ---------------------------------------------------------------------------------------------

const pixelCache = new WeakMap<Texture, RgbaImage | null>()

/** A texture's pixels as RGBA, read once; undefined when its image is not loaded yet. */
export const texturePixels = (texture: Texture | undefined): RgbaImage | undefined => {
    if (!texture || !texture.baseTexture.valid) return undefined
    const known = pixelCache.get(texture)
    if (known !== undefined) return known ?? undefined
    let image: RgbaImage | null = null
    try {
        const source = (texture.baseTexture.resource as any)?.source
        if (source) {
            const canvas = document.createElement("canvas")
            canvas.width = texture.width
            canvas.height = texture.height
            const context = canvas.getContext("2d")
            if (context) {
                const frame = texture.frame, trim = texture.trim
                context.drawImage(source, frame.x, frame.y, frame.width, frame.height,
                    trim?.x || 0, trim?.y || 0, trim?.width || texture.width, trim?.height || texture.height)
                const data = context.getImageData(0, 0, texture.width, texture.height)
                image = { width: data.width, height: data.height, data: new Uint8Array(data.data.buffer.slice(0)) }
            }
        }
    } catch {
        image = null
    }
    pixelCache.set(texture, image)
    return image ?? undefined
}

// ---------------------------------------------------------------------------------------------
// The atlas
// ---------------------------------------------------------------------------------------------

const PAGE = 1024

/**
 * Trimmed art packed onto shared canvas pages (shelves of rows), each page one base texture: what the
 * batch renderer needs to draw a whole castle's distinct cells together. `reset` starts over; a renderer
 * calls it on a full render, when every sprite using the old pages is destroyed.
 */
export class WallTrimAtlas {
    private pages: { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D; base: BaseTexture; x: number; y: number; shelf: number }[] = []

    put(image: RgbaImage): Texture | undefined {
        if (image.width > PAGE || image.height > PAGE) return undefined
        let page = this.pages[this.pages.length - 1]
        const fits = (p: typeof page) => {
            if (p.x + image.width > PAGE) {
                p.x = 0
                p.y += p.shelf
                p.shelf = 0
            }
            return p.y + image.height <= PAGE
        }
        if (!page || !fits(page)) {
            const canvas = document.createElement("canvas")
            canvas.width = PAGE
            canvas.height = PAGE
            const context = canvas.getContext("2d")
            if (!context) return undefined
            page = { canvas, context, base: BaseTexture.from(canvas), x: 0, y: 0, shelf: 0 }
            this.pages.push(page)
        }
        const data = page.context.createImageData(image.width, image.height)
        data.data.set(image.data)
        page.context.putImageData(data, page.x, page.y)
        page.base.update()
        const texture = new Texture(page.base, new Rectangle(page.x, page.y, image.width, image.height))
        page.x += image.width + 1
        page.shelf = Math.max(page.shelf, image.height + 1)
        return texture
    }

    reset(): void {
        const old = this.pages
        this.pages = []
        // The sprites using them are gone with the full render; free the GPU copies a frame later.
        setTimeout(() => old.forEach(p => p.base.destroy()), 0)
    }

    get pageCount(): number {
        return this.pages.length
    }
}

// ---------------------------------------------------------------------------------------------
// The stack a renderer draws, as the painter reads it
// ---------------------------------------------------------------------------------------------

let byItem: { source: readonly WallAutotileDefinition[]; map: Map<string, WallAutotileDefinition> } | undefined
const definitionFor = (itemType: string | undefined): WallAutotileDefinition | undefined => {
    if (!itemType) return undefined
    if (byItem?.source !== WALL_AUTOTILE_DEFINITIONS) byItem = { source: WALL_AUTOTILE_DEFINITIONS, map: new Map(WALL_AUTOTILE_DEFINITIONS.filter(d => d.itemType).map(d => [d.itemType!, d])) }
    return byItem.map.get(itemType)
}

/** Whether any wall material in play draws trims (cheap gate before any work). */
export const anyWallTrims = (): boolean => WALL_AUTOTILE_DEFINITIONS.some(d => !!d.trims)

/** The storeys of a provider's stack, bottom first, and which one the provider is. */
export const storeyStack = (provider: TileMapMetaProvider): { storeys: TileMapMetaProvider[]; index: number } => {
    const below: TileMapMetaProvider[] = []
    const seen = new Set<TileMapMetaProvider>([provider])
    for (let p = provider.getFloorSupportProvider(); p && !seen.has(p); p = p.getFloorSupportProvider()) {
        seen.add(p)
        below.unshift(p)
    }
    const above: TileMapMetaProvider[] = []
    for (let p = provider.getUpperFloorProvider(); p && !seen.has(p); p = p.getUpperFloorProvider()) {
        seen.add(p)
        above.push(p)
    }
    return { storeys: [...below, provider, ...above], index: below.length }
}

/** The state of a stack's walls: changes whenever a wall is built or taken down on any storey of it. */
export const stackVersion = (storeys: TileMapMetaProvider[]): string =>
    storeys.map(p => `${p.roomId}:${p.getPresentationVersion()}`).join("|") + `|${STOREY_RISE_PX}`

/** How the renderer swaps a tile for its seam piece on a given storey (MapRenderingManager.wallSeamTileId). */
export type SeamTile = (x: number, y: number, tilesetTileId: string, context: any, provider: TileMapMetaProvider) => string

/** Whether a storey has floor art at a cell (any floor tile, a stair's or an opening's included). */
export const storeyFloored = (provider: TileMapMetaProvider, x: number, y: number): boolean => {
    const tile = provider.resolveTileMapFromLayerId("map2")?.[x]?.[y]
    return !!tile && tile !== "0"
}

const storeyOf = (provider: TileMapMetaProvider, seamTile: SeamTile): WallTrimStorey => ({
    cells: () => provider.getMapDeltas()
        .filter(d => d.layerId === "sprite" && !d.removed && !!provider.wallItemAt(d.x, d.y) && d.context?.parent?.x === d.x && d.context?.parent?.y === d.y)
        .map(d => ({ x: d.x, y: d.y })),
    wall: (x, y) => definitionFor(provider.wallItemAt(x, y)),
    floored: (x, y) => storeyFloored(provider, x, y),
    piece: (x, y) => {
        const tile = provider.resolveTileMapFromLayerId("sprite")?.[x]?.[y]
        if (!tile || tile === "0" || provider.isHiddenTile(tile)) return undefined
        const context = provider.getMapDeltaAt(x, y, "sprite")?.context
        const parent = context?.parent
        if (!parent) return undefined
        const definition = definitionFor(context?.itemType ?? provider.wallItemAt(parent.x, parent.y))
        if (!definition) return undefined
        const drawn = seamTile(x, y, provider.resolveTilesetTileId(tile), context, provider)
        const image = texturePixels(contentTexture(drawn))
        return image ? { image, parent: { x: parent.x, y: parent.y }, definition } : undefined
    },
})

const groundOf = (provider: TileMapMetaProvider) => (x: number, y: number): WallTrimGround => {
    const px = x * TileSize + TileSize / 2, py = y * TileSize + TileSize / 2
    if (waterAt(provider, px, py)) return "water"
    if (grassAt(provider, px, py)) return "soil"
    const floor = provider.resolveTileMapFromLayerId("map2")?.[x]?.[y]
    return floor && floor !== "0" ? "floor" : "hard"
}

const painters = new WeakMap<TileMapMetaProvider, { version: string; painter: WallTrimPainter }>()

/** The painter for the stack a provider is a storey of, shared by every storey's renderer until a wall of it changes. */
export const wallTrimPainter = (provider: TileMapMetaProvider, seamTile: SeamTile): { painter: WallTrimPainter; storey: number } => {
    const { storeys, index } = storeyStack(provider)
    const bottom = storeys[0]
    const version = stackVersion(storeys)
    let held = painters.get(bottom)
    if (!held || held.version !== version) {
        const stack: WallTrimStack = { storeys: storeys.map(p => storeyOf(p, seamTile)), rise: STOREY_RISE_PX, ground: groundOf(bottom) }
        held = { version, painter: new WallTrimPainter(stack) }
        painters.set(bottom, held)
    }
    return { painter: held.painter, storey: index }
}

export interface BakedWallCell {
    texture: Texture
    overflow: { texture: Texture; dx: number; dy: number }[]
}

/**
 * One wall cell's art with its trims, baked into the atlas: `texture` is what the renderer would draw there
 * (its seams, fringe and edges already applied). Undefined when no trim applies, or its art is not loaded yet.
 */
export const bakeWallCell = (atlas: WallTrimAtlas, provider: TileMapMetaProvider, seamTile: SeamTile, x: number, y: number, texture: Texture): BakedWallCell | undefined => {
    const { painter, storey } = wallTrimPainter(provider, seamTile)
    if (!painter.applies(storey, x, y)) return undefined
    const base = texturePixels(texture)
    if (!base || base.width !== TileSize || base.height !== TileSize) return undefined
    const trimmed = painter.trim(storey, x, y, base)
    if (!trimmed) return undefined
    const baked = atlas.put(trimmed.image)
    if (!baked) return undefined
    const overflow = trimmed.overflow.flatMap(o => {
        const t = atlas.put(o.image)
        return t ? [{ texture: t, dx: o.dx, dy: o.dy }] : []
    })
    return { texture: baked, overflow }
}
